import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useRegister } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { LicenseType, UserRoles } from '../../api/model';
import BrandLogo from '../../components/ui/BrandLogo/BrandLogo';
import { IconArrowLeft } from '@tabler/icons-react';
import styles from './AuthPage.module.scss';

const RegisterPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { showError } = useNotifications();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'USER' as UserRoles,
    licenseType: LicenseType.BASIC,
    customerId: 1 // Default customer ID
  });
  const [errors, setErrors] = useState<{
    username?: string;
    password?: string;
    confirmPassword?: string;
    firstName?: string;
    lastName?: string;
  }>({});

  const registerMutation = useRegister();

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('register.firstNameRequired') || 'First name is required';
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = t('register.firstNameMinLength') || 'First name must be at least 2 characters';
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = t('register.firstNameMaxLength') || 'First name must not exceed 50 characters';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('register.lastNameRequired') || 'Last name is required';
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = t('register.lastNameMinLength') || 'Last name must be at least 2 characters';
    } else if (formData.lastName.length > 50) {
      newErrors.lastName = t('register.lastNameMaxLength') || 'Last name must not exceed 50 characters';
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = t('register.usernameRequired') || 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = t('register.usernameMinLength') || 'Username must be at least 3 characters';
    } else if (formData.username.length > 30) {
      newErrors.username = t('register.usernameMaxLength') || 'Username must not exceed 30 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = t('register.usernameInvalid') || 'Username can only contain letters, numbers, and underscores';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('register.passwordRequired') || 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = t('register.passwordMinLength') || 'Password must be at least 6 characters';
    } else if (formData.password.length > 128) {
      newErrors.password = t('register.passwordMaxLength') || 'Password must not exceed 128 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('register.confirmPasswordRequired') || 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('register.passwordsMismatch') || 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    if (!validateForm()) {
      return;
    }

    // Send register request
    registerMutation.mutate(
      {
        username: formData.username.trim(),
        password: formData.password,
        role: formData.role,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        licenseType: LicenseType.PILOT,
        licenseExpirationDate: '2027-03-10T12:15:50' as any,
        customerId: 1,
      },
      {
        onSuccess: () => {
          // Show success message and redirect to login
          sessionStorage.setItem('registered', 'true');
        },
        onError: (error: any) => {
          console.error('Registration error:', error);
          showError(
            t('register.failed') || 'Registration Failed',
            t('register.genericError') || 'Unable to complete registration. Please try again later.'
          );
        }
      }
    );
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <div className={styles.logoContainer}>
          <div className={styles.brandLogo}>
              <BrandLogo className={styles.brandLogoImage}/>
          </div>
        </div>
        
        <h2 className={styles.authTitle}>{t('register.title')}</h2>
        
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
            <strong>Registration is temporarily disabled.</strong><br />
          </p>
        </div>
        
        <form className={styles.authForm} onSubmit={handleSubmit} style={{ opacity: 0.5, pointerEvents: 'none' }}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="firstName">{t('register.firstName')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => {
                  handleChange(e);
                  if (errors.firstName) setErrors(prev => ({...prev, firstName: undefined}));
                }}
                placeholder={t('register.firstNamePlaceholder')}
                className={errors.firstName ? styles.inputError : ''}
              />
              {errors.firstName && (
                <span className={styles.fieldError}>{errors.firstName}</span>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="lastName">{t('register.lastName')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => {
                  handleChange(e);
                  if (errors.lastName) setErrors(prev => ({...prev, lastName: undefined}));
                }}
                placeholder={t('register.lastNamePlaceholder')}
                className={errors.lastName ? styles.inputError : ''}
              />
              {errors.lastName && (
                <span className={styles.fieldError}>{errors.lastName}</span>
              )}
            </div>
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="username">{t('register.username')}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={(e) => {
                handleChange(e);
                if (errors.username) setErrors(prev => ({...prev, username: undefined}));
              }}
              placeholder={t('register.usernamePlaceholder')}
              className={errors.username ? styles.inputError : ''}
            />
            {errors.username && (
              <span className={styles.fieldError}>{errors.username}</span>
            )}
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="password">{t('register.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={(e) => {
                handleChange(e);
                if (errors.password) setErrors(prev => ({...prev, password: undefined}));
              }}
              placeholder={t('register.passwordPlaceholder')}
              className={errors.password ? styles.inputError : ''}
            />
            {errors.password && (
              <span className={styles.fieldError}>{errors.password}</span>
            )}
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword">{t('register.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={(e) => {
                handleChange(e);
                if (errors.confirmPassword) setErrors(prev => ({...prev, confirmPassword: undefined}));
              }}
              placeholder={t('register.confirmPasswordPlaceholder')}
              className={errors.confirmPassword ? styles.inputError : ''}
            />
            {errors.confirmPassword && (
              <span className={styles.fieldError}>{errors.confirmPassword}</span>
            )}
          </div>
          
          <button 
            type="submit" 
            className={styles.authButton}
            disabled={registerMutation.isPending}
          >
            {registerMutation.isPending ? t('register.registering') : t('register.submit')}
          </button>
        </form>
        
        <div className={styles.divider}>OR</div>
        
        <div className={styles.authLinks}>
          <p>
            {t('register.hasAccount')} <span className={styles.authLink} onClick={() => navigate({ to: '/' })}>{t('register.login')}</span>
          </p>
          <p className={styles.backToHomeLink}>
            <IconArrowLeft size={18} />
            <span className={styles.authLink} onClick={() => navigate({ to: '/' })}>{t('register.backToHome')}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
